import { type ChatRequestOptions, type ChatTransport, type UIMessage, type UIMessageChunk } from 'ai';

export type SendMessagesParams<UI_MESSAGE extends UIMessage = UIMessage> = Parameters<
  ChatTransport<UI_MESSAGE>['sendMessages']
>[0];

export type WebsocketChatTransportOptions = {
  /** Pre-connected WebSocket instance (preferred) */
  ws?: WebSocket;
  /** Callback for tool call results */
  toolCallCallback?: (toolCallResult: unknown) => void;
};

/**
 * WebSocket-based chat transport that communicates with AI SDK agents via WebSocket connections.
 *
 * This transport receives the AI SDK's fullStream from the agent and converts it back
 * to a ReadableStream<UIMessageChunk> for the frontend.
 *
 * Usage:
 * ```typescript
 * // Create connection externally
 * const { ws } = await createRealtimeConnection({ ... });
 *
 * // Pass to transport
 * const transport = new WebsocketChatTransport({ ws });
 * ```
 */
export class WebsocketChatTransport<UI_MESSAGE extends UIMessage> implements ChatTransport<UI_MESSAGE> {
  private readonly pendingStreams = new Map<
    string,
    {
      abortController: AbortController;
      controller: ReadableStreamDefaultController<UIMessageChunk>;
    }
  >();
  private ws: WebSocket | null = null;
  private readonly toolCallCallback: ((toolCallResult: unknown) => void) | null;

  constructor(private readonly options: WebsocketChatTransportOptions = {}) {
    this.toolCallCallback = options.toolCallCallback ?? null;

    if (options.ws) {
      this.attachToWebSocket(options.ws);
    }
  }

  /**
   * Attach to an existing WebSocket connection.
   * This allows external connection management while using the transport for streaming.
   */
  attachToWebSocket(ws: WebSocket): void {
    this.ws = ws;

    // Add our message handler (doesn't replace existing ones since we use addEventListener)
    ws.addEventListener('message', (event) => {
      this.handleWebSocketMessage(event);
    });

    ws.addEventListener('close', () => {
      this.ws = null;
      // Error all pending streams
      for (const [, stream] of this.pendingStreams) {
        stream.controller.error(new Error('WebSocket connection closed'));
        stream.abortController.abort();
      }
      this.pendingStreams.clear();
    });
  }

  /**
   * Get the current WebSocket instance
   */
  getWebSocket(): WebSocket | null {
    return this.ws;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Handles incoming WebSocket messages from the AI SDK agent
   * The agent sends the fullStream chunks directly, so we just forward them
   */
  private handleWebSocketMessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data);

      // Skip non-stream message types (handled by external connection manager)
      if (data.type === 'workspace_state' || data.type === 'pong') {
        return;
      }

      // Find the appropriate stream for this message
      // For now, we'll use the first available stream
      // todo: use an id
      const streamEntry = Array.from(this.pendingStreams.values())[0];
      if (!streamEntry) return;

      const { controller } = streamEntry;

      // The agent sends AI SDK fullStream chunks directly
      // We need to convert them to UIMessageChunk format
      const chunk = data as UIMessageChunk;
      if (chunk) {
        controller.enqueue(chunk);
      }

      // handle tool call results
      if (data.type === 'tool-output-available' && this.toolCallCallback) {
        this.toolCallCallback(data.output);
      }

      // Handle completion events
      if (data.type === 'finish' || data.type === 'error') {
        controller.close();
        // Clean up the stream
        for (const [chatId, stream] of this.pendingStreams) {
          if (stream.controller === controller) {
            this.pendingStreams.delete(chatId);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
      // Error all pending streams
      for (const [, stream] of this.pendingStreams) {
        stream.controller.error(error);
      }
      this.pendingStreams.clear();
    }
  }

  /**
   * Detach from the WebSocket (does not close it)
   */
  detach(): void {
    this.ws = null;
    // Clean up all pending streams
    for (const [, stream] of this.pendingStreams) {
      stream.controller.error(new Error('Transport detached'));
    }
    this.pendingStreams.clear();
  }

  /**
   * Reconnects to an existing stream (not fully implemented for WebSocket)
   * WebSocket connections are stateful, so reconnection is handled at the connection level
   */
  async reconnectToStream({
    chatId,
    ...options
  }: {
    chatId: string;
  } & ChatRequestOptions): Promise<null | ReadableStream<UIMessageChunk>> {
    // For WebSocket, we can't easily reconnect to a specific stream
    // since the connection is stateful. This would require server-side
    // support for stream resumption.
    return null;
  }

  /**
   * Sends messages to the agent via WebSocket and returns a stream of UIMessageChunk
   */
  async sendMessages({
    abortSignal,
    chatId,
    messageId,
    messages,
    trigger,
    ...options
  }: {
    abortSignal: AbortSignal | undefined;
    chatId: string;
    messageId: string | undefined;
    messages: UI_MESSAGE[];
    trigger: 'regenerate-message' | 'submit-message';
  } & ChatRequestOptions): Promise<ReadableStream<UIMessageChunk>> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected. Call attachToWebSocket() first.');
    }

    const abortController = new AbortController();

    // Set up abort handling
    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        abortController.abort();
        this.pendingStreams.delete(chatId);
      });
    }

    // Create the stream
    const stream = new ReadableStream<UIMessageChunk>({
      cancel: () => {
        this.pendingStreams.delete(chatId);
        abortController.abort();
      },
      start: (controller) => {
        this.pendingStreams.set(chatId, { abortController, controller });
      },
    });

    // Create the AI SDK request
    const request: SendMessagesParams<UI_MESSAGE> = {
      abortSignal,
      chatId,
      messageId,
      messages,
      trigger,
      ...options,
    };

    // Send the message to the agent
    this.ws.send(JSON.stringify(request));

    return stream;
  }
}
